/**
 * useMapLayers.ts
 * Base layer functionality for map
 */

import { useDispatch, useSelector } from "react-redux";

import type { Map } from "maplibre-gl";
import type { FeatureCollection, Geometry } from "geojson";

import { SPATIAL_TYPE_TO_LAYER_TYPE } from "@/constants/dataset";
import {
  AREA_INPUT_SOURCE_IDS,
  CATCHMENT_DATASET_POLYGON_SOURCE_ID,
  CIRCE_RADIUS_SOURCE_ID,
  COUNTRY_SOURCE_ID,
  ROUTING_SOURCE_ID,
  SUBLAYERS
} from "@/constants/map";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { type RootState, store } from "@/store";
import {
  reorderDatasets,
  resetDatasetToHistoryDataset
} from "@/store/slice/datasetSlice";
import {
  clearCurrRunningAnalysisId,
  clearInputRowCounts,
  setInputRowCount
} from "@/store/slice/spatialSlice";
import { resetWidgetToHistory } from "@/store/slice/widgetSlice";
import type { SpatialAggregationType } from "@/types/common";
import type { Dataset, DatasetData } from "@/types/dataset";
import { LayerType } from "@/types/map";
import {
  convertDatasetResponseToFeatureCollection,
  getAvailableDatasets
} from "@/utils/dataset";
import { removeAllSourceLayerWithPrefix } from "@/utils/map";

type UseMapLayersProps = {
  map: Map;
  removeAllMarkers: () => void;
  handleAddPointsLayer: (
    dataset: Dataset,
    featureData: FeatureCollection<Geometry>,
    customId?: string
  ) => void;
  handleAddPolygonLayer: (
    dataset: Dataset,
    featureData: FeatureCollection<Geometry>,
    customId?: string
  ) => void;
  handleAddH3GeohashLayer: (
    dataset: Dataset,
    featureData: FeatureCollection<Geometry>,
    customId?: string
  ) => void;
  handleAddLineLayer: (
    dataset: Dataset,
    lineData: FeatureCollection<Geometry>,
    customId?: string
  ) => void;
};

export const useMapLayers = ({
  map,
  removeAllMarkers,
  handleAddPointsLayer,
  handleAddPolygonLayer,
  handleAddH3GeohashLayer,
  handleAddLineLayer
}: UseMapLayersProps) => {
  const dispatch = useDispatch();
  const { isBVTDataForCatchmentEnabled, isAdminTreeEnabled } = useAppFeatures();
  const { datasets, history } = useSelector(
    (state: RootState) => state.dataset
  );
  const availableDatasets = getAvailableDatasets(datasets);

  const showOnlyLayer = (layerIdToKeepVisible: string) => {
    if (!map) return;

    const style = map.getStyle();

    if (!style?.layers) return;
    style.layers.forEach((layer) => {
      // Get the original visibility from the style definition
      const originalVisibility = layer.layout?.visibility || "visible";

      if (
        (layer.id.includes("-fill") ||
          layer.id.includes("-outline") ||
          layer.id.includes("-circle") ||
          layer.id.includes("-count") ||
          layer.id.includes("-unclustered-points") ||
          layer.id.includes("-line-stroke") ||
          layer.id.includes("-label") ||
          layer.id.includes("-rank")) &&
        !layer.id.startsWith(layerIdToKeepVisible)
      ) {
        map.setLayoutProperty(layer.id, "visibility", "none");
      } else if (
        originalVisibility !== "none" ||
        (layer.id.startsWith(layerIdToKeepVisible) &&
          !layer.id.includes("-label") &&
          !layer.id.includes("-rank"))
      ) {
        // Only set to visible if the original style was visible (don't override "none")
        map.setLayoutProperty(layer.id, "visibility", "visible");
      }
    });
  };

  const hideLayersByIds = (layerIdsToHide: string[]) => {
    if (!map) return;

    const style = map.getStyle();

    if (!style?.layers) return;
    style.layers.forEach((layer) => {
      // Get the original visibility from the style definition
      const originalVisibility = layer.layout?.visibility || "visible";

      if (layerIdsToHide.some((id) => layer.id.startsWith(id))) {
        map.setLayoutProperty(layer.id, "visibility", "none");
      } else if (originalVisibility !== "none") {
        // Only set to visible if the original style was visible (don't override "none")
        map.setLayoutProperty(layer.id, "visibility", "visible");
      }
    });
  };

  const handleRemoveSourceLayer = (
    datasetId: string,
    spatialType: SpatialAggregationType
  ) => {
    if (!map) return;

    const layerType = SPATIAL_TYPE_TO_LAYER_TYPE[spatialType];
    const sublayers = SUBLAYERS(datasetId);
    Object.values(sublayers?.[layerType] ?? []).forEach((layer) => {
      if (map?.getLayer(layer)) map.removeLayer(layer);
    });

    const generalSource = map.getSource(datasetId);
    const profilingSource =
      layerType === LayerType.PROFILING &&
      map.getSource(`${datasetId}-polygon`);

    let sourceId: string | null = null;

    if (generalSource) {
      sourceId = datasetId;
    } else if (profilingSource) {
      sourceId = `${datasetId}-polygon`;
    }

    if (sourceId) map.removeSource(sourceId);
  };

  const handleResetLayerOrder = (layerOrders: string[]) => {
    const dispatch = store.dispatch;
    dispatch(reorderDatasets(layerOrders));

    if (!map) return;

    const style = map.getStyle();
    if (!style?.layers) return;

    // Create a mapping of all layers by their source
    const layersBySource: { [sourceId: string]: string[] } = {};
    // The bottom-most area input layer (administrative / catchment boundary)
    // acts as a boundary: dataset layers are moved *before* it so they stay
    // below the area input layers. Stays undefined when there is no area input,
    // which makes moveLayer append to the top as before. ORB-3289.
    let areaInputBeforeId: string | undefined;
    style.layers.forEach((layer) => {
      if ("source" in layer && layer.source) {
        if (
          !areaInputBeforeId &&
          AREA_INPUT_SOURCE_IDS.some((prefix) =>
            layer.source.startsWith(prefix)
          )
        ) {
          areaInputBeforeId = layer.id;
        }
        if (!layersBySource[layer.source]) {
          layersBySource[layer.source] = [];
        }
        layersBySource[layer.source].push(layer.id);
      }
    });

    // Process layers in reverse order of visualization
    // This ensures the first dataset in layerOrders appears on top
    for (let i = layerOrders.length - 1; i >= 0; i--) {
      const sourceId = layerOrders[i];
      const layers = layersBySource[sourceId] || [];

      // Move each layer associated with this source, keeping it below the area
      // input boundary so administrative / catchment layers stay on top.
      layers.forEach((layerId) => {
        map.moveLayer(layerId, areaInputBeforeId);
      });
    }
  };

  const handleHideAllLayerVisibility = () => {
    if (!map) return;

    const style = map.getStyle();

    if (!style?.layers) return;

    // Hide all layers by setting their visibility to 'none'
    style.layers.forEach((layer) => {
      if (
        layer.id.includes("-fill") ||
        layer.id.includes("-outline") ||
        layer.id.includes("-circle") ||
        layer.id.includes("-count") ||
        layer.id.includes("-unclustered-points") ||
        layer.id.includes("-line-stroke")
      ) {
        map.setLayoutProperty(layer.id, "visibility", "none");
      }
    });
  };

  const handleClearAnalysisResultView = () => {
    removeAllMarkers();
    removeAllSourceLayerWithPrefix(map, ROUTING_SOURCE_ID, []);
    removeAllSourceLayerWithPrefix(map, CIRCE_RADIUS_SOURCE_ID, []);
    removeAllSourceLayerWithPrefix(map, COUNTRY_SOURCE_ID, []);
    // Polygon-typed catchment dataset layers (added by useCatchmentArea)
    // belong to the pre-analysis input view. They must be wiped when the
    // analysis result view takes over the map so the H3/geohash grid output
    // isn't drawn on top of the input polygons. Only present when the BVT
    // multi-dataset catchment flow is on.
    if (isBVTDataForCatchmentEnabled) {
      removeAllSourceLayerWithPrefix(
        map,
        CATCHMENT_DATASET_POLYGON_SOURCE_ID,
        []
      );
    }
  };

  const handleResetLayerData = (
    datasets: { [id: string]: Dataset },
    rawDatasets: {
      [id: string]: DatasetData;
    }
  ) => {
    // Get the map instance
    if (!map) return;

    const availableHistoryDatasets = getAvailableDatasets(datasets);

    // Iterate through each dataset and reset its properties
    Object.values(availableHistoryDatasets).forEach((dataset) => {
      const data = rawDatasets[dataset.data.id];

      const featureCollection = convertDatasetResponseToFeatureCollection(
        data,
        dataset.categories
      );
      const spatialType = dataset.properties.spatialType;
      if (spatialType === LayerType.POINT) {
        handleAddPointsLayer(dataset, featureCollection);
      } else if (
        spatialType === LayerType.POLYGON ||
        spatialType === LayerType.ADMINISTRATIVE
      ) {
        handleAddPolygonLayer(dataset, featureCollection);
      } else if (
        spatialType === LayerType.H3 ||
        spatialType === LayerType.GEOHASH
      ) {
        handleAddH3GeohashLayer(dataset, featureCollection);
      } else if (spatialType === LayerType.LINE) {
        handleAddLineLayer(dataset, featureCollection);
      }
    });
  };

  const handleResetDatasetToHistoryDataset = () => {
    // Get the difference between current datasets and history datasets
    const getDifferenceBetweenDatasets = () => {
      const currentDatasetIds = Object.keys(availableDatasets);
      const historyDatasetIds = history ? Object.keys(history.datasets) : [];
      return currentDatasetIds.filter((id) => !historyDatasetIds.includes(id));
    };

    // Calculate differences before resetting
    const addedDatasets = getDifferenceBetweenDatasets();

    // Remove layers for datasets that were added after the history snapshot
    addedDatasets.forEach((datasetId) => {
      const dataset = datasets[datasetId];
      handleRemoveSourceLayer(datasetId, dataset.properties.spatialType);
    });

    dispatch(resetDatasetToHistoryDataset());
    dispatch(resetWidgetToHistory());
    dispatch(clearCurrRunningAnalysisId());
    handleResetLayerOrder(history.visualizationOrder);
    handleResetLayerData(history.datasets, history.rawDatasets);

    // The spatial slice (which holds inputRowCounts) may have been reset while
    // viewing the analysis result, so rebuild the input-rows accumulator from
    // the restored datasets to keep the total-input-rows limit in sync.

    if (isAdminTreeEnabled) {
      dispatch(clearInputRowCounts());
      Object.entries(history.rawDatasets ?? {}).forEach(([id, data]) => {
        dispatch(
          setInputRowCount({
            datasetId: id,
            count: data?.rows?.length ?? 0
          })
        );
      });
    }
  };

  return {
    handleClearAnalysisResultView,
    handleResetDatasetToHistoryDataset,
    handleRemoveSourceLayer,
    handleResetLayerOrder,
    handleHideAllLayerVisibility,
    handleResetLayerData,
    showOnlyLayer,
    hideLayersByIds
  };
};

export default useMapLayers;
